import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { JoinCallModal } from "../elements/JoinCallModal";
import { JoinChatModal } from "../elements/JoinChatModal";
import { useSocket } from "./SocketProvider";
import { setCallerData } from "../../store/slice/callingSlice";
import { getProfileDetails } from "../../service/authService";
import {
  bindRingtoneUnlock,
  bindRingtoneCrossTab,
  startRingtone,
  stopRingtone,
  getRingtoneAudio,
  subscribeRingtoneBlocked,
  subscribeRingtoneStopped,
} from "../../util/ringtone";

/**
 * Global host for incoming Call Now / scheduled ring / chat ring.
 *
 * MUST stay mounted across route changes. Previously JoinCallModal lived only
 * inside DefaultHeader, which unmounts when navigating between layouts
 * (home vs /appointments vs /profile). That made rings appear only on some pages.
 */
function getUserDetails() {
  try {
    return JSON.parse(localStorage.getItem("userDetails") || "{}");
  } catch {
    return {};
  }
}

function getMyId() {
  return String(getUserDetails()?._id || "");
}

export default function IncomingCallHost() {
  const dispatch = useDispatch();
  const socket = useSocket();
  const location = useLocation();
  const callerData = useSelector((s) => s?.callingSlice?.data);

  const [isOpen, setIsOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  const audioRef = useRef(null);
  if (!audioRef.current) {
    audioRef.current = getRingtoneAudio();
  }

  const openApptRef = useRef(null);
  const recoveringRef = useRef(false);

  // Already inside live session UI — do not overlay ring modals
  const onLiveCall =
    /^\/chats(\/|$)/.test(location.pathname) ||
    /^\/text-chat(\/|$)/.test(location.pathname) ||
    /^\/textchat(\/|$)/.test(location.pathname);

  useEffect(() => {
    bindRingtoneUnlock();
    bindRingtoneCrossTab();
  }, []);

  useEffect(() => {
    return subscribeRingtoneStopped(() => {
      setIsOpen(false);
      setIsChatModalOpen(false);
      openApptRef.current = null;
    });
  }, []);

  useEffect(() => {
    if (!(isOpen || isChatModalOpen)) return undefined;
    return subscribeRingtoneBlocked((blocked) => {
      if (blocked) {
        toast.info("Incoming call — tap the screen to enable ringtone (required on iPhone).", {
          autoClose: 8000,
          toastId: "iphone-ring-tap",
        });
      }
    });
  }, [isOpen, isChatModalOpen]);

  useEffect(() => {
    if (isOpen || isChatModalOpen) {
      const apptId =
        appointment?.appointmentId ||
        callerData?.appointmentId ||
        openApptRef.current;
      startRingtone(apptId);
      return () => stopRingtone("modal-close", apptId);
    }
    stopRingtone("modal-closed");
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isChatModalOpen]);

  const openVideoCall = useCallback(
    (data) => {
      if (!data || onLiveCall) return;
      const myId = getMyId();
      if (!myId) return;

      const target =
        data?.consultant?.id ||
        data?.callerData?.consultant?.id ||
        data?.consultantId ||
        data?.consultant?._id ||
        data?.id;
      if (!target || String(myId) !== String(target)) return;

      // Accept video/voice/chat modes and direct-access service
      const mode = data?.mode || data?.service?.mode?.slug;
      const service = data?.service;
      const serviceOk =
        service === "direct-access-online-psychological-counseling" ||
        service?.slug === "direct-access-online-psychological-counseling" ||
        mode === "video" ||
        mode === "voice" ||
        mode === "chat" ||
        !service;

      if (!serviceOk) return;

      const apptId = data?.appointmentId ? String(data.appointmentId) : null;
      if (apptId && openApptRef.current === apptId && isOpen) return;

      if (apptId) {
        sessionStorage.removeItem(`ringDismissed:${apptId}`);
      }
      if (data?.sessionId) setSessionId(data.sessionId);
      if (data?.token || data?.sessionToken) {
        setSessionToken(data.token || data.sessionToken);
      }

      dispatch(setCallerData(data));
      setAppointment(data);
      openApptRef.current = apptId;
      try {
        localStorage.setItem("appointmentData", JSON.stringify(data));
      } catch (_) {
        /* ignore */
      }
      startRingtone(apptId);
      setIsChatModalOpen(false);
      setIsOpen(true);
    },
    [dispatch, onLiveCall, isOpen],
  );

  const openChat = useCallback(
    (data) => {
      if (!data || onLiveCall) return;
      const myId = getMyId();
      if (!myId) return;

      const target =
        data?.consultantId ||
        data?.id ||
        data?.callerData?.consultant?.id ||
        data?.consultant?.id;
      if (!target || String(myId) !== String(target)) return;

      const apptId = data?.appointmentId ? String(data.appointmentId) : null;
      if (apptId) {
        sessionStorage.removeItem(`ringDismissed:${apptId}`);
      }

      dispatch(setCallerData(data));
      setAppointment(data);
      openApptRef.current = apptId;
      try {
        localStorage.setItem("appointmentData", JSON.stringify(data));
      } catch (_) {
        /* ignore */
      }
      startRingtone(apptId);
      setIsOpen(false);
      setIsChatModalOpen(true);
    },
    [dispatch, onLiveCall],
  );

  useSocket("consultantCallResponse", openVideoCall, [openVideoCall]);

  useSocket(
    "consultantIndirectCallResponse",
    (data) => {
      openVideoCall({
        ...data,
        user: data?.callerData?.user || data?.user,
        consultant: data?.callerData?.consultant || data?.consultant,
        consultantId:
          data?.consultantId || data?.id || data?.callerData?.consultant?.id,
      });
    },
    [openVideoCall],
  );

  useSocket("chatSessionResponse", openChat, [openChat]);

  useSocket(
    "chatSessionIndirectResponse",
    (data) => {
      openChat({
        ...data,
        user: data?.callerData?.user || data?.user,
        consultant: data?.callerData?.consultant || data?.consultant,
        consultantId: data?.consultantId || data?.id,
      });
    },
    [openChat],
  );

  useSocket(
    "vonageTokBoxSessionResponse",
    (data) => {
      const myId = getMyId();
      const cId = data?.callerData?.consultant?.id || data?.consultantId;
      if (!myId || !cId || String(myId) !== String(cId)) return;
      if (data?.sessionId) setSessionId(data.sessionId);
      if (data?.token) setSessionToken(data.token);
      setAppointment((prev) => ({ ...(prev || {}), ...data }));
      try {
        const stored = JSON.parse(localStorage.getItem("appointmentData") || "{}");
        localStorage.setItem(
          "appointmentData",
          JSON.stringify({
            ...stored,
            ...data,
            sessionId: data.sessionId || stored.sessionId,
            token: data.token || stored.token,
          }),
        );
      } catch (_) {
        /* ignore */
      }
    },
    [],
  );

  useSocket(
    "chatJoinConfirmResponse",
    (data) => {
      if (data?.joined !== true) return;
      const myId = getMyId();
      const isMine =
        myId &&
        (String(data?.consultantId) === myId ||
          String(data?.userId) === myId ||
          String(data?.clientId) === myId);
      if (isMine || data?.appointmentId) {
        stopRingtone("chat-accepted", data?.appointmentId);
        setIsChatModalOpen(false);
        setIsOpen(false);
        openApptRef.current = null;
      }
    },
    [],
  );

  useSocket(
    "chatSessionRejectResponse",
    (data) => {
      if (!data?.disconnect) return;
      const myId = getMyId();
      if (
        !myId ||
        (String(data?.userId) !== myId && String(data?.consultantId) !== myId)
      ) {
        return;
      }
      setIsChatModalOpen(false);
      setIsOpen(false);
      openApptRef.current = null;
      stopRingtone("reject", data?.appointmentId);
      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
      if (getUserDetails()?.roleCode === "user") {
        toast.info("Consultant is not available try to call again later.", {
          autoClose: 5000,
          toastId: "chat-reject-user",
        });
      }
    },
    [],
  );

  useSocket(
    "vonageSessionRejectResponse",
    (data) => {
      if (!data?.disconnect) return;
      const myId = getMyId();
      if (
        !myId ||
        (String(data?.userId) !== myId && String(data?.consultantId) !== myId)
      ) {
        return;
      }
      setIsOpen(false);
      setIsChatModalOpen(false);
      openApptRef.current = null;
      stopRingtone("reject", data?.appointmentId);
      localStorage.removeItem("appointmentData");
      localStorage.removeItem("joined");
      const role = getUserDetails()?.roleCode;
      if (data?.role === "user" && role === "consultant") {
        toast.info(
          "Client has canceled the call. Stay online as they may try to reach out again.",
          { autoClose: 5000, toastId: "client-cancel-call" },
        );
      } else if (data?.role !== "user" && role === "user") {
        toast.info("Consultant is not available, try to call again later.", {
          autoClose: 10000,
          toastId: "consultant-reject-call",
        });
      }
    },
    [],
  );

  useSocket(
    "videoSessionDisconnectResponse",
    (data) => {
      if (data?.disconnect !== true) return;
      // Only close ring UI here — live call pages handle their own leave
      setIsOpen(false);
      setIsChatModalOpen(false);
      openApptRef.current = null;
      stopRingtone("disconnect", data?.appointmentId);
    },
    [],
  );

  /** Recover ring from DB if socket event was missed (navigation / mobile) */
  const recoverRingFromProfile = useCallback(async () => {
    if (onLiveCall || isOpen || isChatModalOpen || recoveringRef.current) return;
    const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
    if (!token) return;
    if (getUserDetails()?.roleCode !== "consultant") return;

    recoveringRef.current = true;
    try {
      const response = await getProfileDetails(token);
      if (response?.data?.status !== 200) return;
      const docs = response?.data?.docs;
      const RING_MAX_AGE_MS = 90 * 1000;
      const appt = docs?.appointmentData;
      const ringing = docs?.ringingStatus;
      const apptStatus = appt?.status;
      const apptFresh =
        appt?.createdAt || appt?.updatedAt
          ? Date.now() - new Date(appt.updatedAt || appt.createdAt).getTime() <
            RING_MAX_AGE_MS
          : false;
      const isLiveRing =
        ringing?.status === true &&
        appt?.consultantStatus === "notJoin" &&
        (apptStatus === "active" || apptStatus === "inProgress") &&
        apptFresh &&
        sessionStorage.getItem(`ringDismissed:${appt?._id}`) !== "1";

      if (!isLiveRing || !appt?._id) return;

      const Data = {
        user: {
          name: `${appt?.clientDetails?.firstName || ""} ${
            appt?.clientDetails?.lastName || ""
          }`.trim(),
          email: appt?.clientDetails?.email,
          image: appt?.clientDetails?.image,
          id: appt?.clientDetails?._id || appt?.clientId,
        },
        consultant: {
          name: `${appt?.consultantDetails?.firstName || ""} ${
            appt?.consultantDetails?.lastName || ""
          }`.trim(),
          email: appt?.consultantDetails?.email,
          image: appt?.consultantDetails?.image,
          id: appt?.consultantDetails?._id || appt?.consultantId,
        },
        mode: appt?.service?.mode?.slug,
      };
      const finalData = {
        appointmentId: appt?._id,
        consultantId: appt?.consultantId,
        clientId: appt?.clientId,
        appointmentStartTime: appt?.consultantJoinTime || Date.now(),
        mode: appt?.service?.mode?.slug,
        callerData: { ...Data },
        user: Data.user,
        consultant: Data.consultant,
        sessionId: appt?.sessionId,
        token: appt?.sessionToken,
        paymentMode: appt?.paymentMode,
        service: "direct-access-online-psychological-counseling",
      };

      if (ringing?.mode === "chat" || finalData.mode === "chat") {
        openChat(finalData);
      } else {
        openVideoCall(finalData);
      }
    } catch (_) {
      /* ignore */
    } finally {
      recoveringRef.current = false;
    }
  }, [onLiveCall, isOpen, isChatModalOpen, openChat, openVideoCall]);

  // After every route change — still show ring if one is live
  useEffect(() => {
    recoverRingFromProfile();
  }, [location.pathname, recoverRingFromProfile]);

  // Poll backup while consultant is logged in
  useEffect(() => {
    const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
    if (!token) return undefined;
    const id = setInterval(() => {
      if (getUserDetails()?.roleCode === "consultant" && !onLiveCall) {
        recoverRingFromProfile();
      }
    }, 4000);
    return () => clearInterval(id);
  }, [recoverRingFromProfile, onLiveCall]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") recoverRingFromProfile();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [recoverRingFromProfile]);

  useEffect(() => {
    if (!socket) return undefined;
    const onConnect = () => recoverRingFromProfile();
    socket.on("connect", onConnect);
    return () => {
      socket.off("connect", onConnect);
    };
  }, [socket, recoverRingFromProfile]);

  // Hide host UI only on live call pages (listeners above still ran; we skip open)
  if (onLiveCall) {
    return null;
  }

  return (
    <>
      <JoinCallModal
        isOpen={isOpen}
        setIsOpen={(v) => {
          setIsOpen(!!v);
          if (!v) {
            stopRingtone();
            openApptRef.current = null;
          }
        }}
        data={
          callerData?.consultant ||
          callerData?.user ||
          appointment?.consultant ||
          appointment?.user
        }
        callerData={callerData || appointment}
        sessionId={sessionId || callerData?.sessionId || appointment?.sessionId}
        sessionToken={
          sessionToken ||
          callerData?.token ||
          callerData?.sessionToken ||
          appointment?.token ||
          appointment?.sessionToken
        }
        audioRef={audioRef}
        service={"direct-access-online-psychological-counseling"}
      />
      <JoinChatModal
        isOpen={isChatModalOpen}
        setIsOpen={(v) => {
          setIsChatModalOpen(!!v);
          if (!v) {
            stopRingtone();
            openApptRef.current = null;
          }
        }}
        appointmentData={appointment || callerData}
        callerData={callerData}
        audioRef={audioRef}
      />
    </>
  );
}
